import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import Guid from "_shared/guids/guid";
import AssistantCitationSchema, { AssistantCitationJSON, AssistantCitationParams } from "ai-assistant/citations/assistant-citation-schema";

export default class AssistantCitation {
  public static create(values: AssistantCitationParams): AssistantCitation {
    const parsed = AssistantCitationSchema.validate(values);
    if (isNullOrUndefined(parsed)) {
      throw new Error("Invalid AssistantCitation parameters.");
    }
    return new AssistantCitation(parsed);
  }
  
  public static fromJSON(json: AssistantCitationJSON): AssistantCitation {
    if (isNullOrUndefined(json)) return json;
    const params = AssistantCitationSchema.deserialize(json);
    return new AssistantCitation(params);
  }

  /** The number the answer text cites this source by, as [[n]]. */
  public readonly ordinal: number;
  public readonly fileId: Guid; 
  public readonly fileName: string;
  public readonly downloadDocumentId?: Guid;
  public readonly sourcePath?: string;
  public readonly page?: number;
  public readonly nodeId?: Guid;
  public readonly matched?: boolean;
  public readonly text?: string;
  /**
   * Whether the API withheld this source's content because the reader may no longer see it.
   *
   * Only ever set on a conversation read back from history. A citation is a verbatim copy of
   * document content the server re-serves on request, so it re-asks the permission question at
   * read time rather than trusting the answer it had when the answer was written.
   */
  public readonly withheld?: boolean;
  public readonly retrieverScore?: number;
  public readonly semanticScore?: number;

  /**
   * How this citation names its source, or that it cannot.
   *
   * A withheld citation may still name the document when the reader can see it in their
   * documents list - losing content access is not the same as losing sight of the file. When
   * they cannot see it either, the API sends no name and no ids, because naming it would
   * disclose that it exists.
   */
  public get sourceLabel(): string {
    if (this.fileName) return this.fileName;
    return this.withheld ? 'A source you no longer have access to' : 'Untitled source';
  }

  /**
   * A stable React key: two chunks of one document share a file ID but not a node ID.
   *
   * The empty check is load-bearing. An empty Guid is a truthy object, so a citation whose
   * node id the API could not parse used to key on the nil GUID - and every such citation
   * in one answer then shared it, which is a duplicate-key collision rather than a missing
   * key. The ordinal is unique within a turn, so it is what identifies those.
   */
  public get key(): string {
    return this.nodeId && !this.nodeId.isEmpty
      ? this.nodeId.value
      : `${this.fileId.value}-${this.ordinal}`;
  }

  public constructor(params: AssistantCitationParams) {
    this.ordinal = params.ordinal;
    this.fileId = params.fileId;
    this.fileName = params.fileName;
    this.downloadDocumentId = params.downloadDocumentId;
    this.sourcePath = params.sourcePath;
    this.page = params.page;
    this.nodeId = params.nodeId;
    this.matched = params.matched;
    this.text = params.text;
    this.withheld = params.withheld;
    this.retrieverScore = params.retrieverScore;
    this.semanticScore = params.semanticScore;
  }

  public toJSON(): AssistantCitationJSON {
    return AssistantCitationSchema.serialize(this);
  }
  
  public toString(): string {
    return `
      Ordinal: ${this.ordinal},
      File ID: ${this.fileId}, 
      File Name: ${this.fileName},
      Page: ${this.page},
      Node ID: ${this.nodeId},
      Matched: ${this.matched},
      Text: ${this.text}
    `;
  }
}
